import React, { useState } from "react";
import { Search, Edit, Trash2, Plus, X, Save, Loader2, AlertTriangle, Globe } from "lucide-react";
import { Layout } from "../../layouts/DashboardLayout";
import useCountries from "../../hooks/countries/useCountries";
import { useNavigate } from "react-router-dom";
import { Country } from "../../utils/database";

interface FilterState {
  search: string;
}

interface CountryItemProps {
  country: Country;
  onAction: (action: string, country: Country) => void;
}

interface CountriesListProps {
  countries: Country[];
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onCountryAction: (action: string, country: Country) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  endIndex: number;
  totalCountries: number;
}

interface EditCountryModalProps {
  country: Country | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (country: Country) => void;
  isLoading: boolean;
}

interface DeleteConfirmModalProps {
  country: Country | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

// Modal de confirmation de suppression
const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ country, isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen || !country) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Confirmer la suppression</h2>
        </div>

        <div className="mb-6">
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer le pays <strong>{country.name}</strong> ?
          </p>
          <p className="text-sm text-gray-500 mt-2">Cette action est irréversible.</p>
        </div>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Suppression...</span>
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                <span>Supprimer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal d'édition
const EditCountryModal: React.FC<EditCountryModalProps> = ({ country, isOpen, onClose, onSave, isLoading }) => {
  const [form, setForm] = useState({
    id: "",
    code: "",
    name: "",
    phonecode: "",
    devise: "",
  });

  const [errors, setErrors] = useState<Partial<Country>>({});

  // Initialiser le formulaire quand le pays change
  React.useEffect(() => {
    if (country) {
      setForm({
        id: country.id,
        code: country.code,
        name: country.name,
        phonecode: country.phonecode,
        devise: country.devise,
      });
      setErrors({});
    }
  }, [country]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Country> = {};

    if (!form.name.trim()) {
      newErrors.name = "Le nom du pays est requis";
    }

    if (!form.code.trim()) {
      newErrors.code = "Le code du pays est requis";
    } else if (form.code.length !== 2) {
      newErrors.code = "Le code doit contenir exactement 2 caractères";
    }

    if (!form.phonecode.trim()) {
      newErrors.phonecode = "L'indicatif téléphonique est requis";
    } else if (!/^\d+$/.test(form.phonecode)) {
      newErrors.phonecode = "L'indicatif doit contenir uniquement des chiffres";
    }

    if (!form.devise.trim()) {
      newErrors.devise = "La devise est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof Country, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "code" ? value.toUpperCase() : value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(form as Country);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen || !country) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Modifier le Pays</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du Pays *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.name ? "border-red-300" : "border-gray-300"
              }`}
              disabled={isLoading}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code Pays *</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => handleChange("code", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.code ? "border-red-300" : "border-gray-300"
              }`}
              maxLength={2}
              disabled={isLoading}
            />
            {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Indicatif Téléphonique *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">+</span>
              <input
                type="text"
                value={form.phonecode}
                onChange={(e) => handleChange("phonecode", e.target.value)}
                className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.phonecode ? "border-red-300" : "border-gray-300"
                }`}
                disabled={isLoading}
              />
            </div>
            {errors.phonecode && <p className="mt-1 text-sm text-red-600">{errors.phonecode}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Devise *</label>
            <input
              type="text"
              value={form.devise}
              onChange={(e) => handleChange("devise", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.devise ? "border-red-300" : "border-gray-300"
              }`}
              disabled={isLoading}
            />
            {errors.devise && <p className="mt-1 text-sm text-red-600">{errors.devise}</p>}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Modification...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Modifier</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// CountryItem Component
const CountryItem: React.FC<CountryItemProps> = ({ country, onAction }) => {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <p className="font-medium text-gray-800">{country.name}</p>
      </td>
      <td className="px-6 py-4">
        <span className="font-medium">{country.code}</span>
      </td>
      <td className="px-6 py-4">
        <span className="font-medium">+{country.phonecode}</span>
      </td>
      <td className="px-6 py-4">
        <span className="font-medium">{country.devise}</span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onAction("edit", country)}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
            title="Modifier"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onAction("delete", country)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
            title="Supprimer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// CountriesList Component
const CountriesList: React.FC<CountriesListProps> = ({
  countries,
  filters,
  onFiltersChange,
  onCountryAction,
  currentPage,
  totalPages,
  onPageChange,
  startIndex,
  endIndex,
  totalCountries,
}) => {
  const countriesPerPage = 10;
  const startIdx = (currentPage - 1) * countriesPerPage;
  const currentCountries = countries.slice(startIdx, startIdx + countriesPerPage);
  const navigate = useNavigate();

  return (
    <>
      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par pays, code, devise..."
                value={filters.search}
                onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <button
            onClick={() => navigate("/izilife-pay/new-country")}
            className="ml-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter Pays</span>
          </button>
        </div>
      </div>

      {/* Countries Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Nom du Pays</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Code</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Indicatif</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Devise</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentCountries.length > 0 ? (
                currentCountries.map((country) => (
                  <CountryItem key={country.id} country={country} onAction={onCountryAction} />
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Aucun pays trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Affichage {startIndex + 1}-{endIndex} sur {totalCountries} pays
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Main CountriesPage Component
const CountriesPage: React.FC = () => {
  const { countries, deleteCountry, updateCountry, countriesLoading } = useCountries({});

  // State management
  const [filters, setFilters] = useState<FilterState>({
    search: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [deletingCountry, setDeletingCountry] = useState<Country | null>(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const countriesPerPage = 10;

  // Filter logic
  const filteredCountries = (countries || []).filter((country) => {
    return (
      filters.search === "" ||
      country.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      country.code.toLowerCase().includes(filters.search.toLowerCase()) ||
      country.devise.toLowerCase().includes(filters.search.toLowerCase()) ||
      country.phonecode.includes(filters.search)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCountries.length / countriesPerPage);
  const startIndex = (currentPage - 1) * countriesPerPage;
  const endIndex = Math.min(startIndex + countriesPerPage, filteredCountries.length);

  // Event handlers
  const handleCountryAction = (action: string, country: Country) => {
    switch (action) {
      case "edit":
        setEditingCountry(country);
        break;
      case "delete":
        setDeletingCountry(country);
        break;
    }
  };

  const handleEditSave = async (country: Country) => {
    setIsEditLoading(true);
    try {
      await updateCountry(country);
      setEditingCountry(null);
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      alert("Erreur lors de la modification du pays");
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCountry) return;

    setIsDeleteLoading(true);
    try {
      await deleteCountry(deletingCountry.id);
      setDeletingCountry(null);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression du pays");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  if (countriesLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des transactions...</p>
          </div>
        </div>
      </Layout>
    );
  }
  return (
    <Layout>
      <div className="p-2 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Globe className="w-8 h-8 mt-2 text-green-600" />
              Gestion des Pays
            </h1>
            <p className="text-gray-600 mt-1">Gestion des pays supportés par la plateforme</p>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Total des pays</h3>
              <p className="text-3xl font-bold text-green-600">{countries?.length || 0}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Pays filtrés</h3>
              <p className="text-3xl font-bold text-blue-600">{filteredCountries.length}</p>
            </div>
          </div>
        </div>

        {/* Countries List */}
        <CountriesList
          countries={filteredCountries}
          filters={filters}
          onFiltersChange={setFilters}
          onCountryAction={handleCountryAction}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          startIndex={startIndex}
          endIndex={endIndex}
          totalCountries={filteredCountries.length}
        />

        {/* Modals */}
        <EditCountryModal
          country={editingCountry}
          isOpen={!!editingCountry}
          onClose={() => setEditingCountry(null)}
          onSave={handleEditSave}
          isLoading={isEditLoading}
        />

        <DeleteConfirmModal
          country={deletingCountry}
          isOpen={!!deletingCountry}
          onClose={() => setDeletingCountry(null)}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleteLoading}
        />
      </div>
    </Layout>
  );
};

export default CountriesPage;
